import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useStateSelector } from '../../data/global/dataSelectors';
import { AccountActions } from '../../data/global/actions/AccountActions';
import { AccountManagementDataActions } from '../../data/global/actions/AccountManagementActions';
import { Tooltip } from '@tradingblock/components';
import { MarketDataQuestionnaire } from '../../blocks/AccountManagement/modals/MarketDataQuestionnaire';

export enum UpdateMarketDataEntitlementBtnContexts {
  preferences = 'preferences',
  drawer = 'drawer',
}

interface Props {
  buttonElementClassName?: string;
  buttonWrapperClassName?: string;
  context: UpdateMarketDataEntitlementBtnContexts;
  marketDataEntitlementType?: string | null;
  buttonText?: string | JSX.Element;
}

const StyledSpan = styled.span<{ context: UpdateMarketDataEntitlementBtnContexts}>`
  #update-market-data-entitlement-btn-tooltip {
    text-transform: none !important;
    letter-spacing: 0;
    font-weight: normal;
  }

  .update-market-data-entitlement-btn:disabled:hover {
    color: var(--blue);
    opacity: ${params => (params.context === UpdateMarketDataEntitlementBtnContexts.preferences ? '0.65' : '1')};
  }
`;

export const UpdateMarketDataEntitlementBtn: FunctionComponent<Props> = ({
  buttonElementClassName,
  buttonWrapperClassName,
  marketDataEntitlementType,
  context,
  buttonText,
}) => {
  const isPendingMarketDataEntitlementPolicyUpdatePresent = useStateSelector(s =>
    s.account.profile.current &&
    s.account.profile.current.entitlement &&
    s.account.profile.current.entitlement.isPendingMarketDataEntitlementPolicyUpdatePresent);
  const defaultAccountId = useStateSelector(s => s.account.profile.current && s.account.profile.current.defaultAccountId);
  const selectedAccountId = useStateSelector(s => s.account.accountId);

  const [shouldRefetchProfileData, setShouldRefetchProfileData] = useState<boolean>(false);
  const [shouldRefetchAccountManagementDetailsData, setShouldRefetchAccountManagementDetailsData] = useState<boolean>(false);
  const [showMarketDataQuestionnaire, setShowMarketDataQuestionnaire] = useState<boolean>(false);

  const accountIdOfAuthUserIsSameAsSelectedAccountId = defaultAccountId === selectedAccountId;

  const disableButtonAndShowTooltip = useCallback((): boolean => {
    if (isPendingMarketDataEntitlementPolicyUpdatePresent) {
      return true;
    }

    return false;
  }, [isPendingMarketDataEntitlementPolicyUpdatePresent]);

  const determineTooltipText = useCallback((): string => {
    if (isPendingMarketDataEntitlementPolicyUpdatePresent) {
      return 'You cannot submit another entitlement status change until your pending request is complete.';
    }

    return '';
  }, [isPendingMarketDataEntitlementPolicyUpdatePresent]);
  
  const ButtonElement = useMemo(() => 
    <button
      className={`update-market-data-entitlement-btn ${buttonElementClassName ? buttonElementClassName : ''}`}
      onClick={() => toggleMarketDataQuestionnaire()}
      disabled={disableButtonAndShowTooltip()}
    >
      {buttonText || marketDataEntitlementType}
    </button>,
  [marketDataEntitlementType, buttonElementClassName, context, buttonText, disableButtonAndShowTooltip]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (shouldRefetchProfileData) {
      dispatch(AccountActions.requestUserProfile());
      setShouldRefetchProfileData(false);
    }
  }, [shouldRefetchProfileData]);
  
  useEffect(() => {
    if (shouldRefetchAccountManagementDetailsData && accountIdOfAuthUserIsSameAsSelectedAccountId) {
      dispatch(AccountManagementDataActions.requestAccountManagementDetails({ accountId: defaultAccountId }));
      setShouldRefetchAccountManagementDetailsData(false);
    }
  }, [shouldRefetchAccountManagementDetailsData]);

  const toggleMarketDataQuestionnaire = () => {
    setShowMarketDataQuestionnaire(!showMarketDataQuestionnaire);
  };

  return (
    <StyledSpan className={buttonWrapperClassName ? buttonWrapperClassName : ''} context={context}>
      {((context === UpdateMarketDataEntitlementBtnContexts.drawer && marketDataEntitlementType) ||
        (context === UpdateMarketDataEntitlementBtnContexts.preferences && accountIdOfAuthUserIsSameAsSelectedAccountId && marketDataEntitlementType)) && (
        <>
          {disableButtonAndShowTooltip() ?
            <Tooltip
              effect="solid"
              place="top"
              content={determineTooltipText()}
              id="update-market-data-entitlement-btn-tooltip"
              className="txt-sm"
            >
              {ButtonElement}
            </Tooltip>
            : ButtonElement
          }
        </>
      )}
      {showMarketDataQuestionnaire &&
        <MarketDataQuestionnaire
          show={showMarketDataQuestionnaire}
          toggleModal={toggleMarketDataQuestionnaire}
          setShouldRefetchProfileData={setShouldRefetchProfileData}
          setShouldRefetchAccountManagementDetailsData={setShouldRefetchAccountManagementDetailsData}
        />
      }
    </StyledSpan>
  );
};
